import {Alert, Button, Col, message, Row, Space} from "antd";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {FullPageLoader} from "../../../../../components/full-page-loader";
import {OfferingFormAccordionItem} from "../../../../../components/offering-form/accordion/accordion-item";
import {useCreateLiveEventContext} from "../../context/create-live-event";
import {useLiveEvent} from "../../../../../features/live-events";
import {LiveEventCoverImagesFormFields} from "../../../../../components/offering-form/live-events/cover-images";
import {
  useLiveEventCoverImagesDelete,
  useLiveEventUploadCoverImages,
  useCoverImagesWithFallback,
  mapDomainToEmbedded,
} from "../../../../../features/media";
import {CoverImagesItemResponse} from "../../../../../react-query-hooks/products/types";

/**
 * Converts domain CoverImage[] to legacy CoverImagesItemResponse[] format
 * for the embedded fallback in useCoverImagesWithFallback.
 */
function toLegacyCoverImages(
  eventId: number,
  userId: number,
  coverImages: {id: number; mediaUrl: string; createdAt: string}[]
): CoverImagesItemResponse[] {
  return coverImages.map((img) => ({
    id: String(img.id),
    media_url: img.mediaUrl,
    media_type: "image" as const,
    offering_id: eventId,
    offering_type: "live event" as const,
    user_id: userId,
    createdAt: img.createdAt,
    updatedAt: img.createdAt,
  }));
}

export function CreateLiveEventCoverImagesUploadStep() {
  const liveEventUploadCoverImagesMutation = useLiveEventUploadCoverImages();
  const liveEventCoverImagesDeleteMutation = useLiveEventCoverImagesDelete();
  const {
    dispatch,
    state: {formData, apiResponseData},
  } = useCreateLiveEventContext();

  const getLiveEventQuery = useLiveEvent(Number(apiResponseData?.id));

  // v2 cover images fetch with embedded fallback
  const {coverImages, isLoading: isCoverImagesLoading} = useCoverImagesWithFallback({
    offeringType: "live-class",
    offeringId: getLiveEventQuery.data?.id,
    embedded: getLiveEventQuery.data
      ? toLegacyCoverImages(
          getLiveEventQuery.data.id,
          getLiveEventQuery.data.userId,
          getLiveEventQuery.data.coverImages
        )
      : undefined,
  });
  const coverImagesForDisplay = mapDomainToEmbedded(coverImages);

  if (getLiveEventQuery.isLoading || isCoverImagesLoading) {
    return <FullPageLoader />;
  }

  if (getLiveEventQuery.isError) {
    return (
      <Alert
        message="Error"
        description={getLiveEventQuery.error.message}
        type="error"
        showIcon
      />
    );
  }

  function handleCoverImagesItemDelete({
    mediaId,
    offeringId,
  }: {
    mediaId: string;
    offeringId: number;
  }) {
    liveEventCoverImagesDeleteMutation.mutate(
      {mediaId, offeringId},
      {
        onSuccess: () => {
          message.success("Deleted successfully");
        },
      }
    );
  }

  function handleNextStep() {
    dispatch({
      type: "go-to-cohost-form-step",
    });
  }
  return (
    <div>
      <Space size={16} direction="vertical" style={{display: "flex"}}>
        <OfferingFormAccordionItem key={"Cover image"} index={0} title={"Cover image"}>
          <LiveEventCoverImagesFormFields
            accordionIndex={0}
            liveEventData={{
              id: getLiveEventQuery.data!.id,
            }}
            defaultCoverImages={coverImagesForDisplay}
            deleteCoverImage={handleCoverImagesItemDelete}
            isDeleteLoading={liveEventCoverImagesDeleteMutation.isLoading}
            isUploadLoading={liveEventUploadCoverImagesMutation.isLoading}
            offeringId={getLiveEventQuery.data!.id}
            uploadCoverImages={liveEventUploadCoverImagesMutation}
          />
        </OfferingFormAccordionItem>
        <Row gutter={[0, 16]}>
          <Col span={24} sm={12}>
            <StyledServiceButtonWrapper>
              <Button
                className="secondary-btn"
                onClick={() =>
                  dispatch({
                    type: "go-to-checkout-form-step",
                    payload: {
                      ...formData,
                    },
                  })
                }
                block
                size="small"
                type="default"
              >
                Back
              </Button>
              <Button
                block
                type="primary"
                size="small"
                onClick={handleNextStep}
                disabled={
                  liveEventUploadCoverImagesMutation.isLoading ||
                  liveEventCoverImagesDeleteMutation.isLoading ||
                  coverImages.length < 1
                }
              >
                Next
              </Button>
            </StyledServiceButtonWrapper>
          </Col>
        </Row>
      </Space>
    </div>
  );
}
